// Marketing URLs and the metadata that makes them indexable.
//
// The language of a marketing page is a pure function of its URL — never of
// localStorage or navigator. That is what lets each translation be crawled and
// ranked separately, and it is also what keeps the prerendered HTML emitted by
// `scripts/seo-build.js` identical to what the browser renders for the same URL.
//
// English lives at the root: it is the canonical x-default and the most linked URL.

export const SITE_URL = (process.env.REACT_APP_SITE_URL || "https://visioscreening.com").replace(/\/+$/, "");

export const DEFAULT_LANG = "en";

// Kept in sync with LANGUAGES in `@/i18n` by src/lib/seo.test.js — the two lists
// must not drift, or a language would ship without an indexable URL.
export const LANG_PATHS = { en: "/", it: "/it", es: "/es", fr: "/fr", de: "/de" };

export const SEO_LANGS = Object.keys(LANG_PATHS);

// og:locale wants underscores, hreflang wants the bare code.
const OG_LOCALE = { en: "en_GB", it: "it_IT", es: "es_ES", fr: "fr_FR", de: "de_DE" };

export function pathForLang(lang) {
  return LANG_PATHS[lang] || LANG_PATHS[DEFAULT_LANG];
}

/**
 * The language this browser asks for, if we publish it — otherwise null.
 *
 * This does not contradict the rule above: it never decides what language a URL renders in,
 * it decides which URL a visitor who asked for none belongs on. The root is the only page
 * nobody chose — it is the x-default — so it is the only caller; once it has redirected, the
 * URL owns the language again and the prerendered HTML still matches what the browser renders.
 *
 * `navigator.languages` is ordered by preference and `navigator.language` is only its head,
 * so the first entry we publish wins: an en-first visitor with `it` further down gets English.
 */
export function browserLang() {
  if (typeof navigator === "undefined") return null;
  const prefs = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const pref of prefs) {
    const code = String(pref || "").slice(0, 2).toLowerCase();
    if (SEO_LANGS.includes(code)) return code;
  }
  return null;
}

/** Language owning this pathname, or null when the path is not a marketing page. */
export function langFromPath(pathname) {
  const path = String(pathname || "/").replace(/\/+$/, "") || "/";
  const hit = SEO_LANGS.find((l) => LANG_PATHS[l] === path);
  return hit || null;
}

export function absUrl(path) {
  return `${SITE_URL}${path === "/" ? "/" : path}`;
}

export function canonicalFor(lang) {
  return absUrl(pathForLang(lang));
}

export function ogLocale(lang) {
  return OG_LOCALE[lang] || OG_LOCALE[DEFAULT_LANG];
}

/** hreflang alternates for every language, plus x-default pointing at the root. */
export function alternatesFor() {
  return [
    ...SEO_LANGS.map((lang) => ({ hreflang: lang, href: canonicalFor(lang) })),
    { hreflang: "x-default", href: canonicalFor(DEFAULT_LANG) },
  ];
}

// --- Privacy policy -------------------------------------------------------------
// A second prerendered cluster, hanging off each landing URL: /privacy, /it/privacy, …
// It is derived from LANG_PATHS rather than listed separately so adding a language can
// never ship a landing page whose footer links to a policy that does not exist.
//
// Its hreflang cluster is its own: a policy page must point at the other *policy* pages,
// never at the landing ones, or Google sees a cluster whose members disagree.

export const PRIVACY_SLUG = "privacy";

export function privacyPathForLang(lang) {
  const base = pathForLang(lang);
  return base === "/" ? `/${PRIVACY_SLUG}` : `${base}/${PRIVACY_SLUG}`;
}

/** Language owning this privacy pathname, or null when it is not a policy page. */
export function langFromPrivacyPath(pathname) {
  const path = String(pathname || "/").replace(/\/+$/, "") || "/";
  return SEO_LANGS.find((l) => privacyPathForLang(l) === path) || null;
}

/**
 * Language owning any prerendered URL — landing or policy.
 *
 * `langFromPath` stays landing-only on purpose: it is what `canonicalFor` round-trips
 * against. This is the wider lookup that `detectLang` needs, so that /de/privacy renders
 * in German for a visitor whose stored preference says otherwise — same reason the landing
 * URLs win over localStorage.
 */
export function langFromAnyPath(pathname) {
  return langFromPath(pathname) || langFromPrivacyPath(pathname);
}

export function privacyCanonicalFor(lang) {
  return absUrl(privacyPathForLang(lang));
}

export function privacyAlternatesFor() {
  return [
    ...SEO_LANGS.map((lang) => ({ hreflang: lang, href: privacyCanonicalFor(lang) })),
    { hreflang: "x-default", href: privacyCanonicalFor(DEFAULT_LANG) },
  ];
}
